import { CashRegisterCommandAnswer } from "@ac-expo/api-types";
import { CashRegisterPaymentMethod } from "@prisma/client";
import { z } from "zod";

export const cashRegisterHolderSchema = z.object({
  firstName: z.string().min(1, { message: "Veuillez entrer un nom" }),
  lastName: z.string().min(1, { message: "Veuillez entrer un prénom" }),
  identifier: z.string().max(10).min(10, { message: "Veuillez entrer un identifiant" }),
  password: z.string().optional(),
});

export type CashRegisterHolderForm = z.infer<typeof cashRegisterHolderSchema>;

export const cashRegisterAvailableEntriesSchema = z.object({
  ids: z.array(z.string()),
});

export type CashRegisterAvailableEntriesForm = z.infer<typeof cashRegisterAvailableEntriesSchema>;

export const cashRegisterCustomBlockSchema = z.object({
  id: z.string(),
  isBadge: z.boolean(),
  color: z.string(),
  x: z.number(),
  y: z.number(),
  isHalf: z.boolean(),
  name: z.string(),
  price: z.number(),
  page: z.number(),
});

export const cashRegisterCustomBlocksSchema = z.object({
  blocks: z.array(cashRegisterCustomBlockSchema),
});

export type CashRegisterCustomBlocksForm = z.infer<typeof cashRegisterCustomBlocksSchema>;

export const cashRegisterCommandItemSchema = z.object({
  entryId: z.string(),
  isBadge: z.boolean(),
  quantity: z.number(),
  price: z.number(),
});

export const cashRegisterCommandAnswerSchema = z.object({
  questionId: z.string().min(1),
  answer: z.string().nullish(),
  answerList: z.array(z.string()).nullish(),
}) satisfies z.ZodType<CashRegisterCommandAnswer>;

export const cashRegisterCommandSchema = z.object({
  number: z.number(),
  items: z.array(cashRegisterCommandItemSchema),
  medium: z.nativeEnum(CashRegisterPaymentMethod),
  value: z.number(),
  // Cash register form answers, one per question, collected once per sale before the payment
  answers: z.array(cashRegisterCommandAnswerSchema).optional(),
});

export type CashRegisterCommandForm = z.infer<typeof cashRegisterCommandSchema>;

export const cashRegisterCurrenciesSchema = z.object({
  oneCent: z.number(),
  twoCent: z.number(),
  fiveCent: z.number(),
  tenCent: z.number(),
  twentyCent: z.number(),
  fiftyCent: z.number(),
  oneEuro: z.number(),
  twoEuro: z.number(),
  fiveEuro: z.number(),
  tenEuro: z.number(),
  twentyEuro: z.number(),
  fiftyEuro: z.number(),
  oneHundredEuro: z.number(),
  twoHundredEuro: z.number(),
  fiveHundredEuro: z.number(),
});

export const defaultCashRegisterCurrenciesValues = {
  oneCent: 0,
  twoCent: 0,
  fiveCent: 0,
  tenCent: 0,
  twentyCent: 0,
  fiftyCent: 0,
  oneEuro: 0,
  twoEuro: 0,
  fiveEuro: 0,
  tenEuro: 0,
  twentyEuro: 0,
  fiftyEuro: 0,
  oneHundredEuro: 0,
  twoHundredEuro: 0,
  fiveHundredEuro: 0,
};

export const cashRegisterStateSchema = cashRegisterCurrenciesSchema.extend({});

export const cashRegisterChangeSchema = cashRegisterCurrenciesSchema.extend({
  isAddition: z.boolean(),
});

export type CashRegisterCurrenciesForm = z.infer<typeof cashRegisterCurrenciesSchema>;
export type CashRegisterStateForm = z.infer<typeof cashRegisterStateSchema>;
export type CashRegisterChangeForm = z.infer<typeof cashRegisterChangeSchema>;

export const UpsertCashRegisterSchema = z.object({
  id: z.string().optional(),
  name: z
    .string()
    .min(1, "Le nom de la caisse est requis")
    .max(64, "Le nom de la caisse ne doit pas dépasser 64 caractères"),
});

export type UpsertCashRegisterForm = z.infer<typeof UpsertCashRegisterSchema>;
